import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export interface NavigationShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  route: string;
  description: string;
}

const defaultShortcuts: NavigationShortcut[] = [
  { key: 'd', ctrl: true, shift: true, route: '/', description: 'Go to Dashboard' },
  { key: 'i', ctrl: true, shift: true, route: '/inventory/items', description: 'Go to Inventory' },
  { key: 'p', ctrl: true, shift: true, route: '/purchase-orders', description: 'Go to Purchase Orders' },
  { key: 's', ctrl: true, shift: true, route: '/sales-orders', description: 'Go to Sales Orders' },
  { key: 'r', ctrl: true, shift: true, route: '/reports', description: 'Go to Reports' },
  { key: 'a', ctrl: true, shift: true, route: '/accounting/chart-of-accounts', description: 'Go to Accounts' },
  { key: 'j', ctrl: true, shift: true, route: '/accounting/journal-entries', description: 'Go to Journal Entries' },
  { key: 'h', ctrl: true, shift: true, route: '/navigation', description: 'Go to Navigation Hub' },
  { key: 'c', ctrl: true, shift: true, route: '/customers', description: 'Go to Customers' },
  { key: 'v', ctrl: true, shift: true, route: '/vendors', description: 'Go to Vendors' },
  { key: 'b', ctrl: true, shift: true, route: '/banking/accounts', description: 'Go to Banking' },
  { key: 'e', ctrl: true, shift: true, route: '/expenses', description: 'Go to Expenses' },
];

export function useNavigationShortcuts(enabled = true) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Allow Ctrl+K for search even in input fields
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
          // Let NavigationSearch handle this
          return;
        }
        return;
      }

      // Check for matching shortcut
      for (const shortcut of defaultShortcuts) {
        const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : true;
        const shiftMatch = shortcut.shift ? e.shiftKey : true;
        const altMatch = shortcut.alt ? e.altKey : true;
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

        if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
          // Prevent default only for our shortcuts with modifiers
          if (shortcut.ctrl || shortcut.shift || shortcut.alt) {
            e.preventDefault();
            navigate(shortcut.route);
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, navigate]);

  return { shortcuts: defaultShortcuts };
}
